import * as React from 'react';
import styled from 'styled-components';
import OtpInput from 'react18-input-otp';
import useAsyncAction from '../../../../commonComponents/Hooks';
import { SmsSecurityCodeVerificationModel } from '../../../../models/AccountModels';
import {
	sendSmsSecurityCodeToAuthorisedUser,
	verifySmsSecurityCode,
	verifySmsSecurityCodeAndUnlockProfile
} from '../../../../core/api';
import { loginPageUrl } from '../../../../core/constants';
import { borderGrey, errorRed, newErrorRed, whiteColour } from '../../../cssConstants';
import { sendSmsCodeToMobile } from './MobileNumberConfirmationForm';
import TwoFactorAuthenticationResult from '../../../../models/TwoFactorAuthenticationResult';
import { ApiResultMessages } from '../../../../core/enums';
import { isLoggedIn } from '../../../../core/localStorage';
import { SubTitle, Title, TitleWrapper } from '../../common/PageTitle';
import AppForm from '../../common/AppForm';
import { useNavigateWithAppName } from '../../../common/NavigateWithAppName';
import useClientContextStore from '../../../../zustandStore/clientContextStore';

const SmsVerificationForm = ({
	email,
	mobileNumber,
	securityToken,
	onVerifySuccess,
	isFromLogin,
	onInitialSms
}: Props) => {
	const navigate = useNavigateWithAppName();
	const clientDetails = useClientContextStore((state) => state.clientDetails);
	const [otp, setOtp] = React.useState('');
	const [errorMessage, setErrorMessage] = React.useState<string>(null);
	const [initialSms, setInitialSms] = React.useState(onInitialSms);
	const performAsyncAction = useAsyncAction();
	const { signal } = new AbortController();

	const handleSubmit = async () => {
		if (otp.length !== 6) {
			setErrorMessage('Incorrect code. Please try again.');
			return;
		}
		const model: SmsSecurityCodeVerificationModel = {
			email,
			mobileNumber,
			otp,
			securityToken,
			isFromLogin
		};
		const apiResult = isLoggedIn()
			? await verifySmsSecurityCodeAndUnlockProfile(otp, signal)
			: await verifySmsSecurityCode(model, signal);
		if (!apiResult.succeeded && apiResult.response === ApiResultMessages.TimeoutOrUnauthorised)
			navigate(loginPageUrl, {
				state: { message: 'Mobile number confirmation timeout, please login and setup later.' } // TODO: Consider showing this in login page
			});
		else if (
			(!apiResult.succeeded && apiResult.response === ApiResultMessages.SmsVerificationUnexpectedError) ||
			(apiResult.succeeded && !(apiResult.response as TwoFactorAuthenticationResult).isValidOtp)
		) {
			setErrorMessage('Incorrect code. Please try again.');
		} else {
			onVerifySuccess(apiResult.response as TwoFactorAuthenticationResult);
		}
	};

	const handleResendCode = async () => {
		await performAsyncAction(async () => {
			if (isLoggedIn())
				await sendSmsSecurityCodeToAuthorisedUser(signal); // For unlocking profile
			else await sendSmsCodeToMobile(email, mobileNumber, securityToken, navigate, signal);
		});
	};

	React.useEffect(() => {
		if (initialSms) {
			handleResendCode();
		}
	}, [initialSms]);

	return (
		<>
			<TitleWrapper>
				<Title>We&apos;ve sent you a text message</Title>
				<SubTitle>
					We&apos;ve sent a 6-digit code to **** *** {(mobileNumber ?? clientDetails?.mobilePhone)?.slice(-3)}. Please
					enter the code below.
				</SubTitle>
				<SubTitle>The code will expire in 10 minutes.</SubTitle>
			</TitleWrapper>
			<AppForm
				onSubmit={handleSubmit}
				onBackButtonClick={handleResendCode}
				primaryButtonText="Continue"
				secondaryButtonText="Resend SMS Code"
				singleColumn
			>
				{/* // TODO: replace react18-input-otp as it is old and has console warning */}
				<CustomOtpInput
					containerStyle={{ justifyContent: 'space-between' }}
					value={otp}
					onChange={setOtp}
					numInputs={6}
					separator={renderSeparator}
					shouldAutoFocus
					isInputNum
					$hasError={!!errorMessage}
				/>
				{errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
			</AppForm>
		</>
	);
};

export default SmsVerificationForm;

interface Props {
	email?: string;
	mobileNumber?: string;
	securityToken?: string;
	onVerifySuccess: (result: TwoFactorAuthenticationResult) => void;
	isFromLogin?: boolean;
	onInitialSms?: boolean;
}

export const renderSeparator = (index: number) => {
	if (index === 2) {
		return <span style={{ margin: '0 15px', fontSize: '16px' }} />;
	}
	return <span style={{ margin: '0 5px', fontSize: '16px' }} />;
};

const ErrorMessage = styled.p`
	color: ${newErrorRed};
	font-size: 10px;
	margin: 5px 0 0 0;
	text-align: left;
`;

const CustomOtpInput = styled(OtpInput)<{ $hasError: boolean }>`
	input {
		font-size: 24px;
		font-weight: 300;
		border: 1px solid ${(props) => (props.$hasError ? errorRed : borderGrey)};
		border-radius: 12px;
		background-color: ${whiteColour};
		width: 47px !important;
		height: 48px;

		&:focus-visible {
			outline: 1px solid ${(props) => (props.$hasError ? errorRed : borderGrey)};
		}

		@media (min-width: 437px) {
			width: 50px !important;
			height: 50px;
		}
	}
`;
